/**
 * Params va inputs tekshiruvi — dasturchi xatolari (MODEL_GUIDE 2-band): throw.
 * Talaba xatolari (R < Rmin, flooding, xD ≤ xF) bu yerda EMAS — steady.ts warnings beradi.
 */
import type { RectificationInputs, RectificationParams } from './types';

const frac01 = (v: number | undefined): boolean => v !== undefined && v > 0 && v < 1;

export function validateParams(p: RectificationParams): void {
  if (p.mode !== 'design' && p.mode !== 'rating')
    throw new Error('rectification-column: mode must be "design" or "rating"');
  if (p.lightComponent === p.heavyComponent)
    throw new Error('rectification-column: components must differ');
  if (!(p.pressure > 0)) throw new Error('rectification-column: pressure must be > 0');
  if (!(p.diameter > 0)) throw new Error('rectification-column: diameter must be > 0');
  if (!(p.traySpacing > 0)) throw new Error('rectification-column: traySpacing must be > 0');
  const E = p.trayEfficiency ?? 1;
  if (!(E > 0 && E <= 1)) throw new Error('rectification-column: trayEfficiency must be in (0, 1]');
  if (p.cSb !== undefined && !(p.cSb > 0)) throw new Error('rectification-column: cSb must be > 0');
  if (p.surfaceTension !== undefined && !(p.surfaceTension > 0))
    throw new Error('rectification-column: surfaceTension must be > 0');
  const fdc = p.downcomerAreaFraction ?? 0;
  if (!(fdc >= 0 && fdc < 1))
    throw new Error('rectification-column: downcomerAreaFraction must be in [0, 1)');
  if (p.mode === 'rating') {
    if (!(p.stages !== undefined && Number.isInteger(p.stages) && p.stages >= 2))
      throw new Error('rectification-column: rating requires integer stages ≥ 2');
    const NF = p.feedStage;
    if (!(NF !== undefined && Number.isInteger(NF) && NF >= 1 && NF <= p.stages - 1))
      throw new Error('rectification-column: feedStage must be an integer in [1, stages − 1]');
  }
}

export function validateInputs(p: RectificationParams, i: RectificationInputs): void {
  if (!(i.feedFlow >= 0 && Number.isFinite(i.feedFlow)))
    throw new Error('rectification-column: feedFlow must be ≥ 0');
  if (!frac01(i.xF)) throw new Error('rectification-column: xF must be in (0, 1)');
  if (!(i.refluxRatio >= 0)) throw new Error('rectification-column: refluxRatio must be ≥ 0');
  if (i.feedQuality !== undefined && !Number.isFinite(i.feedQuality))
    throw new Error('rectification-column: feedQuality must be finite');
  if (i.feedTemperature !== undefined && !(i.feedTemperature > 0))
    throw new Error('rectification-column: feedTemperature must be > 0 K');
  if (i.xD !== undefined && !frac01(i.xD))
    throw new Error('rectification-column: xD must be in (0, 1)');
  if (i.xW !== undefined && !frac01(i.xW))
    throw new Error('rectification-column: xW must be in (0, 1)');
  if (i.distillateFraction !== undefined && !frac01(i.distillateFraction))
    throw new Error('rectification-column: distillateFraction must be in (0, 1)');
  if (p.mode === 'design') {
    if (i.xD === undefined || i.xW === undefined)
      throw new Error('rectification-column: design mode requires xD and xW');
    if (!(i.xD > i.xW)) throw new Error('rectification-column: design mode requires xD > xW');
  } else if ((i.distillateFraction === undefined) === (i.xD === undefined)) {
    throw new Error(
      'rectification-column: rating mode requires exactly one of distillateFraction or xD',
    );
  }
}
